import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, getErrorMessage, type PublicPricing } from '../api/client';
import './PricingPage.css';

const PricingPage: React.FC = () => {
  const { t } = useTranslation();
  const [pricing, setPricing] = useState<PublicPricing | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await api.public.getPricing();
        setPricing(response.data);
      } catch (err) {
        setError(getErrorMessage(err));
      } finally {
        setIsLoading(false);
      }
    };

    fetchPricing();
  }, []);

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat('cs-CZ', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatStorage = (mb: number) => {
    if (mb >= 1000) return `${mb / 1000} GB`;
    return `${mb} MB`;
  };

  if (isLoading) {
    return (
      <div className="pricing-page">
        <div className="pricing-loading">
          <i className="fas fa-spinner fa-spin"></i>
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !pricing) {
    return (
      <div className="pricing-page">
        <div className="pricing-error">
          <i className="fas fa-exclamation-circle"></i>
          <p>{error || 'Failed to load pricing'}</p>
          <button onClick={() => window.location.reload()} className="btn btn--primary">
            {t('common.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  const { currency, basePrice, includedActivities, includedMembers, includedStorageMB, addons } = pricing;

  return (
    <div className="pricing-page">
      {/* Header */}
      <header className="pricing-header">
        <Link to="/" className="pricing-back-link">
          <i className="fas fa-arrow-left"></i> {t('pricing.backToHome')}
        </Link>
      </header>

      {/* Hero */}
      <section className="pricing-hero">
        <h1>{t('pricing.title')}</h1>
        <p>{t('pricing.subtitle')}</p>
      </section>

      {/* Single base plan + add-ons */}
      <section className="pricing-plans">
        <div className="pricing-single">
          {/* Base plan card */}
          <div className="pricing-card pricing-card-highlighted">
            <div className="pricing-card-header">
              <h2>{t('pricing.base.name')}</h2>
              <p className="pricing-card-description">{t('pricing.base.description')}</p>
            </div>

            <div className="pricing-card-price">
              <span className="pricing-price-value">{formatPrice(basePrice, currency)}</span>
              <span className="pricing-price-interval">/ {t('pricing.intervals.month')}</span>
            </div>

            <div className="pricing-card-limits">
              <div className="pricing-limit-item">
                <i className="fas fa-layer-group"></i>
                <span>{t('pricing.base.activities', { count: includedActivities })}</span>
              </div>
              <div className="pricing-limit-item">
                <i className="fas fa-users"></i>
                <span>{t('pricing.base.members', { count: includedMembers })}</span>
              </div>
              <div className="pricing-limit-item">
                <i className="fas fa-database"></i>
                <span>{t('pricing.base.storage', { storage: formatStorage(includedStorageMB) })}</span>
              </div>
            </div>

            <div className="pricing-card-action">
              <Link to="/demo" className="btn btn--block btn--primary">
                {t('pricing.tryDemo')}
              </Link>
            </div>
          </div>

          {/* Add-ons */}
          <div className="pricing-addons">
            <h2 className="pricing-addons-title">{t('pricing.addons.title')}</h2>
            <p className="pricing-addons-subtitle">{t('pricing.addons.subtitle')}</p>
            <div className="pricing-addons-grid">
              <div className="pricing-addon-item">
                <i className="fas fa-users"></i>
                <div className="pricing-addon-text">
                  <span className="pricing-addon-label">
                    {t('pricing.addons.memberBlock', { count: addons.memberBlock.size })}
                  </span>
                  <span className="pricing-addon-price">
                    {formatPrice(addons.memberBlock.price, currency)} / {t('pricing.intervals.month')}
                  </span>
                </div>
              </div>
              <div className="pricing-addon-item">
                <i className="fas fa-layer-group"></i>
                <div className="pricing-addon-text">
                  <span className="pricing-addon-label">{t('pricing.addons.activity')}</span>
                  <span className="pricing-addon-price">
                    {formatPrice(addons.activity.price, currency)} / {t('pricing.intervals.month')}
                  </span>
                </div>
              </div>
            </div>
            <p className="pricing-addons-spend-limit">{t('pricing.spendLimit')}</p>
            <p className="pricing-addons-example">{t('pricing.example')}</p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="pricing-info">
        <h2>{t('pricing.faq.title')}</h2>
        <div className="pricing-faq-grid">
          <div className="pricing-faq-item">
            <h3>{t('pricing.faq.q1.question')}</h3>
            <p>{t('pricing.faq.q1.answer')}</p>
          </div>
          <div className="pricing-faq-item">
            <h3>{t('pricing.faq.q2.question')}</h3>
            <p>{t('pricing.faq.q2.answer')}</p>
          </div>
          <div className="pricing-faq-item">
            <h3>{t('pricing.faq.q3.question')}</h3>
            <p>{t('pricing.faq.q3.answer')}</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pricing-cta">
        <h2>{t('pricing.cta.title')}</h2>
        <p>{t('pricing.cta.subtitle')}</p>
        <Link to="/demo" className="btn btn--primary btn--lg">
          {t('pricing.cta.button')}
        </Link>
      </section>
    </div>
  );
};

export default PricingPage;
